import { FC, useMemo } from 'react';
import { Order, Cart } from 'src/trpc';
import { safeMoneyFromCents } from '@libs/services/money';
import { PRICE_PER_TON_IN_CENT } from 'src/constants';
import { ContainerBreakdown } from '@libs/schemas';

interface Props {
  order: Order;
  cart: Cart;
  finalWeight?: number;
  additionalMaterials?: { [materialId: string]: number };
  allAvailableMaterials?: { _id: string; extraChargeInCents: number; name?: string }[];
  containers?: { _id?: string; containerTypeInfo: { size: string; name: string } }[];
  usageByContainer?: { [containerId: string]: number };
  containerCostCents?: number;
  containerBreakdown?: ContainerBreakdown[];
}

export const PayAsYouGoChargeSummary: FC<Props> = ({
  order,
  cart,
  finalWeight = 0,
  additionalMaterials = {},
  allAvailableMaterials = [],
  containers = [],
  usageByContainer = {},
  containerCostCents,
}) => {
  // Extract Pay As You Go pricing details from quotedPricingDetails
  const payAsYouGoDetails = useMemo(() => {
    const containerPricing = order.quotedPricingDetails?.quote?.pricing?.containers?.[0];
    const materialPricing = order.quotedPricingDetails?.quote?.pricing?.materials;

    return {
      perTonRateInCents:
        materialPricing?.perTonRateInCents || order.pricePerTonInCents || PRICE_PER_TON_IN_CENT,
      dailyRateInCents:
        containerPricing?.totalDays && containerPricing?.adjustedPriceInCents
          ? Math.round(containerPricing.adjustedPriceInCents / containerPricing.totalDays)
          : 0,
    };
  }, [order]);

  // Calculate weight cost adjustment
  const weightCost = finalWeight * payAsYouGoDetails.perTonRateInCents;
  const weightCostAdjustment = weightCost - (cart.lineItems?.materials || 0);

  return (
    <>
      {/* Weight Adjustments */}
      {finalWeight > 0 && (
        <div className="flex flex-row justify-between w-full font-light text-xs text-muted">
          <h6>Final Weight: {finalWeight.toFixed(2)} ton(s)</h6>
          <span>{safeMoneyFromCents(weightCostAdjustment).toFormat()}</span>
        </div>
      )}

      {/* Container Costs - Show if we have containerCostCents from backend OR containers to calculate */}
      {(containerCostCents !== undefined || containers.length > 0) &&
        (() => {
          // Use pre-calculated adjustment from backend if available (for completed orders)
          // Otherwise calculate the total cost (for waste processing flow)
          const containerCost =
            containerCostCents !== undefined
              ? containerCostCents
              : containers.reduce((acc, container) => {
                  const days = usageByContainer[container._id!] || 0;
                  const dailyRate = payAsYouGoDetails.dailyRateInCents;
                  const actualCost = days * dailyRate;
                  // Calculate adjustment: actual cost - original cost paid
                  const originalCost =
                    cart.lineItems?.containers?.reduce((sum, c) => sum + c.totalInCents, 0) || 0;
                  return acc + (actualCost - originalCost);
                }, 0);

          return (
            <div className="flex flex-row justify-between w-full font-light text-xs text-muted">
              <h6>Dumpster Costs</h6>
              <span>{safeMoneyFromCents(containerCost).toFormat()}</span>
            </div>
          );
        })()}

      {/* Additional Materials */}
      {allAvailableMaterials &&
        Object.keys(additionalMaterials).map((materialId) => {
          const material = allAvailableMaterials.find((m) => m._id === materialId);
          const quantity = additionalMaterials[materialId] || 0;
          const cost = quantity * (material?.extraChargeInCents || 0);
          if (quantity === 0) return null;
          return (
            <div
              className="flex flex-row justify-between w-full font-light text-xs text-muted"
              key={materialId}
            >
              <h6>{material?.name || 'Unknown Material'}</h6>
              <span>{safeMoneyFromCents(cost).toFormat()}</span>
            </div>
          );
        })}
    </>
  );
};
